import { AUDIO } from '@config/constants';
import { isIOS } from '@engine/hand/device';
import { PluckVoice } from './PluckVoice';
import { TIMBRE_PRESETS, type TimbrePreset, getTimbreByKey } from './timbres';

export interface AudioEngineOptions {
  volume?: number;
}

/**
 * Top-level audio engine.
 *
 * iOS silent-switch workaround: route the master bus through a
 * MediaStreamDestination and feed it to a hidden <audio playsinline>
 * element. iOS classifies <audio> output as "media" (like YouTube),
 * which bypasses the physical mute/silent switch. Desktop browsers
 * output directly to `ctx.destination`.
 */
export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private iosAudioEl: HTMLAudioElement | null = null;
  private iosStreamDest: MediaStreamAudioDestinationNode | null = null;
  /** Shared wet bus for voices that opt in via their timbre preset
   *  (preset.wet > 0). A short feedback delay gives an ambient tail
   *  without the CPU cost of a convolution reverb. */
  private wetIn: GainNode | null = null;

  private voices = new Set<PluckVoice>();
  private handBuckets = new Map<string, Set<PluckVoice>>();
  /** Active bend contribution per hand (in cents). Summed to a global offset. */
  private bendContrib = new Map<string, number>();

  private _volume = AUDIO.VOLUME.DEFAULT;
  private _timbre: TimbrePreset = TIMBRE_PRESETS[0]!;

  constructor(opts: AudioEngineOptions = {}) {
    if (opts.volume !== undefined) this._volume = opts.volume;
  }

  /** Synchronous — safe to call inside a user gesture handler. */
  initialize(): void {
    if (this.ctx) return;
    const Ctor = (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext) as typeof AudioContext;
    this.ctx = new Ctor({
      latencyHint: AUDIO.LATENCY_HINT,
      sampleRate: AUDIO.DEFAULT_SAMPLE_RATE,
    });

    if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }

    this.limiter = this.ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -6;
    this.limiter.knee.value = 6;
    this.limiter.ratio.value = 10;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.1;

    this.master = this.ctx.createGain();
    this.master.gain.value = this._volume;

    this.limiter.connect(this.master);
    this.master.connect(this.ctx.destination);

    // Wet bus: delay + feedback approximation of a small room. Voices
    // with preset.wet > 0 send a fraction of their amp output here.
    this.wetIn = this.ctx.createGain();
    const wetDelay = this.ctx.createDelay(1.0);
    wetDelay.delayTime.value = 0.12;
    const wetFeedback = this.ctx.createGain();
    wetFeedback.gain.value = 0.45;
    const wetLP = this.ctx.createBiquadFilter();
    wetLP.type = 'lowpass';
    wetLP.frequency.value = 3600;
    this.wetIn.connect(wetDelay);
    wetDelay.connect(wetLP);
    wetLP.connect(this.limiter);
    wetLP.connect(wetFeedback);
    wetFeedback.connect(wetDelay);

    if (isIOS()) {
      this.wireIosSilentSwitchBypass();
    }

    this.playSilentUnlock();
  }

  private wireIosSilentSwitchBypass(): void {
    if (!this.ctx || !this.master) return;

    this.iosStreamDest = this.ctx.createMediaStreamDestination();
    this.master.connect(this.iosStreamDest);

    const audio = document.createElement('audio');
    audio.srcObject = this.iosStreamDest.stream;
    audio.playsInline = true;
    audio.autoplay = true;
    audio.controls = false;
    audio.muted = false;
    audio.style.position = 'fixed';
    audio.style.width = '0';
    audio.style.height = '0';
    audio.style.opacity = '0';
    audio.style.pointerEvents = 'none';
    document.body.appendChild(audio);
    this.iosAudioEl = audio;

    const play = () => audio.play().catch(() => { /* retry on next gesture */ });
    play();
    window.addEventListener('pointerdown', play, { passive: true });
    window.addEventListener('touchstart', play, { passive: true });
  }

  private playSilentUnlock(): void {
    if (!this.ctx) return;
    try {
      const buffer = this.ctx.createBuffer(1, 1, 22050);
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(this.ctx.destination);
      source.start(0);
    } catch {
      /* non-fatal */
    }
  }

  isReady(): boolean {
    return this.ctx !== null && this.master !== null;
  }

  /** The shared AudioContext, once `initialize()` has run. The Transport
   *  / Metronome / Recorder modules all key their timing and signal
   *  routing off this — they must NOT create their own context. */
  get audioContext(): AudioContext | null {
    return this.ctx;
  }

  /** Pre-limiter bus node — anything that wants to ride the master
   *  compressor (metronome clicks, future cue tones) connects here.
   *  Recorder taps off this so a recorded mix sounds the same as
   *  what the speaker hears. */
  get outputBus(): AudioNode | null {
    return this.limiter;
  }

  tryResume(): void {
    if (this.ctx && this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
    if (this.iosAudioEl && this.iosAudioEl.paused) {
      this.iosAudioEl.play().catch(() => {});
    }
  }

  /** Cut all voices + pause background audio. Call on page hide. */
  suspend(): void {
    this.hardCutAll();
    this.iosAudioEl?.pause();
    if (this.ctx && this.ctx.state === 'running') {
      void this.ctx.suspend();
    }
  }

  get outputLatencyMs(): number {
    if (!this.ctx) return 0;
    const base = this.ctx.baseLatency ?? 0;
    const out = this.ctx.outputLatency ?? 0;
    return Math.round((base + out) * 1000);
  }

  setVolume(v: number): void {
    this._volume = Math.max(AUDIO.VOLUME.MIN, Math.min(AUDIO.VOLUME.MAX, v));
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(this._volume, this.ctx.currentTime, 0.02);
    }
  }

  setTimbre(keyOrPreset: string | TimbrePreset): void {
    this._timbre =
      typeof keyOrPreset === 'string' ? getTimbreByKey(keyOrPreset) : keyOrPreset;
  }
  get timbreKey(): string {
    return this._timbre.key;
  }

  pluck(
    _stringIndex: number,
    frequency: number,
    velocity: number,
    handKey: string,
    /** Audio-time at which the pluck should sound. If omitted or in
     *  the past, it sounds immediately. Quantize uses this so a snapped
     *  pluck lands on the same audio-time as the metronome click. */
    startTime?: number,
  ): void {
    if (!this.ctx || !this.limiter) return;
    this.tryResume();

    while (this.voices.size >= AUDIO.MAX_POLYPHONY) {
      const oldest = this.voices.values().next().value as PluckVoice | undefined;
      if (!oldest) break;
      // Voice-steal for polyphony: fade instead of chopping so the
      // recycled voice doesn't click against the incoming pluck.
      oldest.release(220);
      this.voices.delete(oldest);
    }

    const voice = new PluckVoice(
      this.ctx,
      this.limiter,
      frequency,
      velocity,
      this.wetIn ?? undefined,
      this._timbre,
      startTime,
    );
    this.voices.add(voice);

    const bucket = this.handBuckets.get(handKey) ?? new Set<PluckVoice>();
    bucket.add(voice);
    this.handBuckets.set(handKey, bucket);

    voice.setOnEnded(() => {
      this.voices.delete(voice);
      bucket.delete(voice);
    });
  }

  /** Called once when bend mode ENGAGES for a hand. Ensures any ringing
   *  voice on that hand stays audible for the duration of the bend — a
   *  half-decayed voice was being bent silently before, which felt broken.
   *  No-op if no voices are currently ringing on this hand. */
  engageBend(handKey: string): void {
    const bucket = this.handBuckets.get(handKey);
    if (!bucket) return;
    for (const v of bucket) {
      if (v.isAlive()) v.holdBendSustain();
    }
  }

  bend(handKey: string, cents: number): void {
    const bucket = this.handBuckets.get(handKey);
    if (!bucket) return;
    for (const v of bucket) {
      if (v.isAlive()) v.glideToCents(cents);
    }
  }

  /** Smoothly ramp this hand's voices back to neutral pitch. Uses a
   *  longer glide than live bend updates so the snapback is audible
   *  ("dây ramp về neutral, khá nhanh") instead of a pitch cliff. */
  releaseBend(handKey: string): void {
    const bucket = this.handBuckets.get(handKey);
    if (!bucket) return;
    for (const v of bucket) {
      if (v.isAlive()) {
        v.glideToCents(0, AUDIO.BEND.RELEASE_MS);
        v.releaseBendHold();
      }
    }
  }

  mute(handKey: string): void {
    const bucket = this.handBuckets.get(handKey);
    if (!bucket) return;
    // Soft release so the tail fades through the ADSR-style shape
    // instead of chopping. Voices keep their own onEnded self-cleanup.
    for (const v of bucket) v.release();
    bucket.clear();
  }

  muteAll(): void {
    for (const v of this.voices) v.release();
    this.voices.clear();
    this.handBuckets.clear();
  }

  /** Hard-cut path for page-hide / dispose — we're about to suspend or
   *  close the context, so scheduled ramps wouldn't complete anyway. */
  private hardCutAll(): void {
    for (const v of this.voices) v.cut(30);
    this.voices.clear();
    this.handBuckets.clear();
  }

  dispose(): void {
    this.hardCutAll();
    this.master?.disconnect();
    this.limiter?.disconnect();
    this.iosStreamDest?.disconnect();
    if (this.iosAudioEl) {
      this.iosAudioEl.pause();
      this.iosAudioEl.srcObject = null;
      this.iosAudioEl.remove();
      this.iosAudioEl = null;
    }
    void this.ctx?.close();
    this.ctx = null;
    this.master = null;
    this.limiter = null;
    this.iosStreamDest = null;
  }
}
